import axios from 'axios';
import { v4 } from 'uuid';
import {
  BIG_PICTURE_URL,
  AUTH_HEADER_KEY,
  KARROTAUTH_HTTP,
  USER_URL,
  XPROPERTY_URL,
  BUSINESS_AUTH_URL,
  BIZ_PLATFORM_CENTER,
} from 'constants/config';
import { getImageInfo, imageCompress } from 'utils/imageParser';
import { openToast } from 'utils/localDispatcher';
import { store } from 'store';
import { getSupportRegion } from './snippet';
import { jwtDecoder } from 'utils/auth/tokenUtils';
import { generateKarrotUserAgent } from 'utils/userAgentHelper';
import { UserInfoResponse } from 'types/apiTypes';
import { Response, Status } from 'external/rocket-chat/api/api_pb';
import { getBizAccountQuery } from 'utils/protobuf/query';
import { encodeAuthentication } from 'utils/socket/authentication';
import { restOrigin, currentAuthentication } from 'utils/macroBackup';
import { logLevel } from 'utils/consoleLevel';
import * as Sentry from '@sentry/browser';

// 화면을 숨긴 채 기다리는 인증·소유 확인 요청의 대기 한도.
export const AUTH_REQUEST_TIMEOUT_MS = 10_000;
// 토큰 갱신 요청의 상한. 화면 대기와 무관하게 갱신 락 해제를 보장한다.
const TOKEN_REFRESH_TIMEOUT_MS = 30_000;

const bizPlatformCenterInstance = axios.create({
  withCredentials: true,
  baseURL: BIZ_PLATFORM_CENTER,
});

interface imageUploadReqestProps {
  fileItem: File
  userData: {
    id: number
    auth_token: string
  }
  channelId: string
}
export const imageUploadReqest = async ({ fileItem, userData, channelId }: imageUploadReqestProps) => {
  const fileData = await imageCompress(fileItem);
  const { width, height } = await getImageInfo(fileData);
  const bizToken = store.getState().authStore.bizToken;
  const qrLoginToken = store.getState().authStore.storeAccessToken;
  const oidcAccessToken = store.getState().authStore.oidcAccessToken;

  if (bizToken) {
    const requestBody = new FormData();
    requestBody.append('body', fileData);
    requestBody.append('region', getSupportRegion());
    requestBody.append('service', 'rocketchat');
    requestBody.append('type', 'chat');
    requestBody.append('width', `${width}`);
    requestBody.append('height', `${height}`);
    requestBody.append('contentType', fileItem.type);
    requestBody.append('filepath', `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`);
    try {
      const { data } = await bizPlatformCenterInstance.post('/api/v1/images', requestBody);
      return {
        ...data.data,
        width,
        height,
      };
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    }
  } else if (oidcAccessToken) {
    const requestBody = {
      user_id: Number(userData.id),
      service: 'rocketchat',
      type: 'chat',
      width: width,
      height: height,
      filepath: `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`,
      content_type: fileItem.type,
    };
    const requestOption = {
      headers: {
        'Authorization': `Bearer ${oidcAccessToken}`,
        'Content-Type': 'application/json',
      },
    };
    try {
      const { data } = await axios.post(
        `${BIG_PICTURE_URL.replace(':region', 'kr')}/api/v2/request_upload_url`,
        requestBody,
        requestOption,
      );
      if (data) {
        await axios.put(data.data.image.upload_url, fileData, {
          headers: {
            'Content-Type': fileItem.type,
            'X-Amz-Meta-Id': data.data.image.id,
          },
        });
        return data.data.image;
      }
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    } 
  } else {
    const requestBody = {
      user_id: Number(userData.id),
      service: 'rocketchat',
      type: 'chat',
      width: width,
      height: height,
      filepath: `rocketchat/${channelId}/${v4()}.${fileItem.type.replace('image/', '')}`,
      content_type: fileItem.type,
    };
    const requestOption = {
      headers: {
        [AUTH_HEADER_KEY]: qrLoginToken,
        'Content-Type': 'application/json',
      },
    };
    try {
      const { data } = await axios.post(
        `${BIG_PICTURE_URL.replace(':region', 'kr')}/api/v2/request_upload_url`,
        requestBody,
        requestOption,
      );
      if (data) {
        await axios.put(data.data.image.upload_url, fileData, {
          headers: {
            'Content-Type': fileItem.type,
            'X-Amz-Meta-Id': data.data.image.id,
          },
        });
        return data.data.image;
      }
    } catch (_) {
      openToast('파일 전송에 실패했어요');
    }
  }
};

export const AuthAPI = {
  tokenRefresh: async (refToken: string, region: string) => {
    // 화면 대기 제한은 호출부(appInit)의 데드라인이 맡는다. 여기 상한은 갱신 락이 세션 내내 잠기지 않게 하는 안전장치로,
    // 서버가 리프레시 토큰을 회전시킨 뒤 응답만 늦는 경우 새 토큰을 잃지 않도록 데드라인보다 넉넉하게 둔다.
    return axios.post(
      `${KARROTAUTH_HTTP.replace(':region', region)}/v2/auth/refreshtokens`,
      { refresh_token: refToken },
      { timeout: TOKEN_REFRESH_TIMEOUT_MS },
    );
  },
  getUserInfo: async <T extends 'oidc' | 'qr' | undefined>(
    accessToken: string, 
    region: string, 
    tokenType?: T
  ): Promise<UserInfoResponse<T>> => {

    // NOTE(capel): OIDC 토큰 로그인 방식
    if (tokenType === 'oidc') {
      const xUserAgent = generateKarrotUserAgent(
        navigator.userAgent,
        '23.46.00', // APP_VERSION은 고정한다.
        process.env.NODE_ENV !== 'production'
      );
      const decodedToken = jwtDecoder(accessToken);
      const headers = {
        'Authorization': `Bearer ${accessToken}`,
        'X-User-Agent': xUserAgent
      };
      return axios.get(
        `${USER_URL.replace(':region', region)}/user/v3/karrot-users/${decodedToken?.sub}`,
        {
          headers,
          params: {
            'query_option.include_karrot_user_profile': true
          }
        }
      );
    }
    
    // NOTE(capel): 기존 QR 로그인 방식
    const headers = { 
      'x-user-token': accessToken
    };
    return axios.get(`${KARROTAUTH_HTTP.replace(':region', region)}/v2/auth/u`, {
      headers,
    });
  },
};

export const XPropertyAPI = {
  badWords: async () => {
    return axios.get(`${XPROPERTY_URL}/v1/countries/KR/properties/chat_bad_words_regex`);
  },
};

const bizPlatformAuthInstance = axios.create({
  withCredentials: true,
  baseURL: BUSINESS_AUTH_URL,
  // 인증 판정이 끝나기 전에는 화면을 숨기므로, 응답이 멈춘 연결에서 무한정 기다리지 않도록 제한한다.
  timeout: AUTH_REQUEST_TIMEOUT_MS,
});
export const businessAuthAPI = {
  getBizToken: () => {
    return bizPlatformAuthInstance.get('/api/v2/auths/tokens');
  },
  getProfiles: () => {
    return bizPlatformAuthInstance.get('/api/v1/profiles');
  },
};

export const getStickers = () => {
  return axios.get('/sticker.json');
};

export const RocketChatRestAPI = {
  // 웹소켓 없이 소유 비즈계정 ID 목록을 받는다. 웹소켓과 같은 Request protobuf를 HTTP fallback으로 보낸다.
  // 세션 토큰이 없거나(로그인 전) 실패하면 null을 반환하고, 실패는 Sentry로 보고해 "소유하지 않음"과 구분되게 한다.
  getBizAccountIds: async (): Promise<number[] | null> => {
    const authentication = currentAuthentication();
    if (!authentication) {
      return null;
    }
    try {
      const { data } = await axios.post(
        `${restOrigin(getSupportRegion())}/api/v2/chat?${encodeAuthentication(authentication)}`,
        getBizAccountQuery().serializeBinary(),
        // 소유 확인이 끝나기 전에는 화면을 숨기므로, 응답이 멈추면 실패로 처리해 기존 화면으로 돌아간다.
        {
          headers: { 'Content-Type': 'application/x-protobuf' },
          responseType: 'arraybuffer',
          timeout: AUTH_REQUEST_TIMEOUT_MS,
        },
      );
      const res = Response.toObject(false, Response.deserializeBinary(new Uint8Array(data)));
      if (res.status !== Status.OK) {
        throw new Error(`getBizAccountIds failed: status=${res.status} message=${res.statusMessage}`);
      }
      return (res.getBizAccounts?.bizAccountsList || []).map((item) => item.id);
    } catch (error) {
      logLevel.log(error);
      // Sentry ignoreErrors는 부분 문자열 일치라 axios 메시지('Network Error', 'Request failed …')가 message에 있으면 버려진다.
      // 원인은 extra로만 보내고, 요청 URL에 세션 토큰이 실리므로 에러 객체 자체는 첨부하지 않는다.
      Sentry.captureMessage('getBizAccountIds failed', {
        level: Sentry.Severity.Error,
        tags: { feature: 'biz-chat-redirect' },
        extra: { reason: error instanceof Error ? error.message : String(error) },
      });
      return null;
    }
  },
};
